import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Section } from "@/types/models/page";

interface WebBuilderState {
  sectionsById: Record<string, Section>;
  sectionOrder: string[]; // ordered list of section ids
  selectedSectionId: string | null;
  previewHiddenSectionIds: string[]; // sections hidden for preview only (UI state)
}

const initialState: WebBuilderState = {
  sectionsById: {},
  sectionOrder: [],
  selectedSectionId: null,
  previewHiddenSectionIds: [],
};

const webbuilderSlice = createSlice({
  name: "webbuilder",
  initialState,
  reducers: {
    setSections(state, action: PayloadAction<Section[]>) {
      const list = action.payload || [];
      const byId: Record<string, Section> = {};
      const order: string[] = [];
      list.forEach((s, idx) => {
        // clone the incoming section to avoid mutating payload or frozen objects
        byId[s.id] = {
          ...s,
          position: typeof s.position === "number" ? s.position : idx,
        };
        order.push(s.id);
      });
      state.sectionsById = byId;
      state.sectionOrder = order;
    },
    setSelectedSection(state, action: PayloadAction<Section | null>) {
      state.selectedSectionId = action.payload ? action.payload.id : null;
    },
    addSection(
      state,
      action: PayloadAction<
        { section: Section; insertAtIndex?: number } | Section
      >,
    ) {
      // Support both old and new payload formats for backward compatibility
      const isNewFormat =
        action.payload &&
        typeof action.payload === "object" &&
        "section" in action.payload;
      const sectionData = isNewFormat
        ? (action.payload as { section: Section; insertAtIndex?: number })
            .section
        : (action.payload as Section);
      const insertAtIndex = isNewFormat
        ? (action.payload as { section: Section; insertAtIndex?: number })
            .insertAtIndex
        : undefined;

      console.log("🔴 Redux addSection reducer called with:", sectionData);
      console.log("🔴 Insert at index:", insertAtIndex);
      console.log("🔴 Current state before:", {
        sectionOrderLength: state.sectionOrder.length,
        sectionsByIdCount: Object.keys(state.sectionsById).length,
      });

      // Generate a unique temporary ID for unpublished sections to avoid conflicts
      // Use timestamp + random to ensure uniqueness even for rapid additions
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Determine the insertion index
      const finalInsertIndex =
        insertAtIndex !== undefined
          ? Math.min(Math.max(0, insertAtIndex), state.sectionOrder.length)
          : state.sectionOrder.length;

      // Create a new section with a unique temporary ID for new/unsaved sections
      const newSection = {
        ...sectionData,
        id: tempId,
        position: finalInsertIndex,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_hidden: false,
        is_locked: false,
      };

      console.log("🔴 New section created:", {
        id: newSection.id,
        section_identifier: newSection.section_identifier,
        position: newSection.position,
        is_hidden: newSection.is_hidden,
        finalInsertIndex,
      });

      state.sectionsById[newSection.id] = newSection;

      // Insert at the specified index
      state.sectionOrder.splice(finalInsertIndex, 0, newSection.id);

      // Update positions for all sections after insertion
      state.sectionOrder.forEach((id, idx) => {
        const sec = state.sectionsById[id];
        if (sec) sec.position = idx;
      });

      // Automatically select the newly added section
      state.selectedSectionId = newSection.id;

      console.log("🔴 State after update:", {
        sectionOrderLength: state.sectionOrder.length,
        sectionsByIdCount: Object.keys(state.sectionsById).length,
        sectionOrder: state.sectionOrder,
      });
    },
    deleteSection(state, action: PayloadAction<string>) {
      const sectionId = action.payload;
      delete state.sectionsById[sectionId];
      state.sectionOrder = state.sectionOrder.filter((id) => id !== sectionId);

      // If the deleted section was selected, clear selection
      if (state.selectedSectionId === sectionId) {
        state.selectedSectionId = null;
      }
    },
    toggleSectionVisibility(state, action: PayloadAction<string>) {
      const sectionId = action.payload;
      const section = state.sectionsById[sectionId];
      if (section) {
        section.is_hidden = !section.is_hidden;
        // Update the selected section if it's the same one
        if (state.selectedSectionId === sectionId) {
          // selectedSection is derived via selector
        }
      }
    },
    toggleSectionLock(state, action: PayloadAction<string>) {
      const sectionId = action.payload;
      const section = state.sectionsById[sectionId];
      if (section) {
        section.is_locked = !section.is_locked;
      }
    },
    updateSection(state, action: PayloadAction<Section>) {
      console.log("Redux updateSection called:", {
        sectionId: action.payload.id,
        componentDataKeys: Object.keys(action.payload.component_data || {}),
        componentData: action.payload.component_data,
      });

      const id = action.payload.id;
      if (state.sectionsById[id]) {
        // clone payload to ensure the stored section is mutable within Immer
        state.sectionsById[id] = { ...action.payload };
      } else {
        console.warn("Section not found in store for ID:", id);
      }
      if (state.selectedSectionId === id) {
        // selectedSection will be derived from selectedSectionId
      }
    },
    reorderSections(
      state,
      action: PayloadAction<{ draggedId: string; targetId: string }>,
    ) {
      const { draggedId, targetId } = action.payload;
      const draggedIndex = state.sectionOrder.indexOf(draggedId);
      const targetIndex = state.sectionOrder.indexOf(targetId);
      if (draggedIndex === -1 || targetIndex === -1) return;
      // remove
      state.sectionOrder.splice(draggedIndex, 1);
      const insertIndex =
        draggedIndex < targetIndex ? targetIndex : targetIndex + 1;
      state.sectionOrder.splice(insertIndex, 0, draggedId);
      // Update positions in byId
      state.sectionOrder.forEach((id, idx) => {
        const sec = state.sectionsById[id];
        if (sec) sec.position = idx;
      });
    },
    toggleSectionPreviewVisibility(state, action: PayloadAction<string>) {
      const sectionId = action.payload;
      const index = state.previewHiddenSectionIds.indexOf(sectionId);
      if (index > -1) {
        state.previewHiddenSectionIds.splice(index, 1);
      } else {
        state.previewHiddenSectionIds.push(sectionId);
      }
    },
    setPreviewHiddenSectionIds(state, action: PayloadAction<string[]>) {
      state.previewHiddenSectionIds = Array.from(new Set(action.payload || []));
    },
  },
});

export const {
  setSections,
  setSelectedSection,
  addSection,
  deleteSection,
  toggleSectionVisibility,
  toggleSectionLock,
  updateSection,
  reorderSections,
  toggleSectionPreviewVisibility,
  setPreviewHiddenSectionIds,
} = webbuilderSlice.actions;

export default webbuilderSlice.reducer;

// Selectors
export const selectSectionsArray = (state: any) =>
  (state.webbuilder.sectionOrder || [])
    .map((id: string) => state.webbuilder.sectionsById[id])
    .filter(Boolean);

export const selectSelectedSection = (state: any) => {
  const id = state.webbuilder.selectedSectionId;
  return id ? state.webbuilder.sectionsById[id] || null : null;
};

export const selectSectionById = (state: any, id: string) =>
  state.webbuilder.sectionsById[id] || null;
